// Display-only date rendering for the Business settings (/account) pages.
// Backend ISO strings are formatted for reading, never computed against rate data.

// '12 March 2026' — long form for created/joined dates.
export function formatLongDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

// '2 Jul 2026, 14:05' — absolute datetime for audit/activity meta lines.
export function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// Coarse relative-future label from a backend ISO timestamp: 'in a few minutes' ·
// 'in about 40 minutes' · 'in about 1 hour'. Display-only — it reads a server-computed
// instant (e.g. the next credential probe) and formats a friendly countdown; it performs
// no rate/monetary math. Past/invalid timestamps read as 'shortly'.
export function formatRelativeSoon(iso: string | null): string {
  if (!iso) return 'shortly'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'shortly'

  const minutes = Math.round((d.getTime() - Date.now()) / 60000)
  if (minutes <= 1) return 'in a few minutes'
  if (minutes < 55) return `in about ${minutes} minutes`
  const hours = Math.round(minutes / 60)
  return `in about ${hours} ${hours === 1 ? 'hour' : 'hours'}`
}

// Relative-ish short form for operational freshness columns:
// 'Today 14:05' · 'Yesterday 14:05' · '2 Jul, 14:05' (year appended when not current).
export function formatShortWhen(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'

  const now = new Date()
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

  if (sameDay(d, now)) return `Today ${time}`

  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(d, yesterday)) return `Yesterday ${time}`

  const day = d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  })
  return `${day}, ${time}`
}
